import React, {
    ReactElement,
    useRef,
    useState,
    useEffect,
    FocusEvent,
    InputHTMLAttributes,
} from 'react';

import useActiveElement from '../../../hooks/useActiveElement';
import {
    getFormattedDate,
    isDateValid,
} from '../../../utils/acquisition-utils';
import isElementIncluded from './components/InputDate/util/isElementIncluded';
import InputDate from './components/InputDate/InputDate';

interface IVisitedRef {
    day: boolean;
    month: boolean;
    year: boolean;
    isInside: boolean;
}
export interface IDateOfBirthFieldProps {
    ctaId?: string;
    label?: string | ReactElement | ReactElement[];
    className?: string;
    inputClass: string;
    //inputClass required for targeting inputs with the class. Can be optional once CL is refactored
    iconContainerClass?: string;
    helperText?: string;
    alternate?: boolean;
    required?: boolean;
    error?: boolean;
    errorText?: string;
    requiredMessage?: string;
    invalidErrorMessage?: string;
    ageMax?: number;
    ageMin?: number;
    valid: [boolean, (arg?: any) => void];
    isValid?: (val: string | number) => boolean;
    readOnly?: boolean;
    noErrorsOrHelper?: boolean;
    name?: string;
    id?: string;
    value: string;
    setValueCallback?: THandlerFunction;
    onBlurCallback?: (value: FocusEvent<HTMLInputElement>) => void;
    onFocusCallback?: (value: FocusEvent<HTMLInputElement>) => void;
    validateWithEnter?: boolean; //pqde submit with enter
    applicantStateOrAu?: string;
    useInputChild?: boolean;
    removeAsterisk?: boolean;
    showSuccessIcon?: boolean;
    useSuccessIcon?: boolean;
    isAuthorizedUser?: boolean;
    ref?: React.MutableRefObject<any>;
    advancedMasking?: boolean;
}
//OnMouseDown is needed to determine that the focus is inside of another input.
//the validation happens when the active element changes, and this active element doesn't
//go from input to input, passes first to body, so it's validating as if we were focusing out of the
//date input. The secuence is mousedown/blur/activeelement/activelememntagain-theinputthistime/mouseup. If we go on blur with mousedown
//click, means that we are in another input. Otherwise onBlur happens without clicking on any of the fields.
type THTMLInputElementClear = HTMLInputElement & {
    clearFields?: (clear: boolean) => void;
};
const DateOfBirthField = (
    props: IDateOfBirthFieldProps & InputHTMLAttributes<any>,
) => {
    const inputRef = useRef<THTMLInputElementClear | null>(props.ref?.current ?? null);
    const [inputInteracted, setInputInteracted] = useState<boolean>(false);
    const [clickedInOtherDateField, setClickedInOtherDateField] =
        useState<boolean>(false);
    const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
    const [isTabKey, setIsTabKey] = useState<boolean>(false);
    const activeElement = useActiveElement();
    const [showIcon, setShowIcon] = useState(false);
    const [isDOBElementFocused, setIsDOBElementFocused] = useState<Boolean>(false);
    const minAgeForStateOrAuthorized: number = props.applicantStateOrAu === 'au' ? 15 : 18;

    const dobObj = useRef<Record<string, any>>({
        month: '',
        day: '',
        year: '',
    });
    const visitedRef = useRef<IVisitedRef>({
        day: false,
        month: false,
        year: false,
        isInside: false,
    });

    //need to target each field of date input
    useEffect(() => {
        const dobInputs = document.getElementsByClassName(props.inputClass);
        if (dobInputs.length) {
            dobObj.current = {
                month: dobInputs[0],
                day: dobInputs[1],
                year: dobInputs[2],
            };
        }
    }, []);

    useEffect(() => {
        const allInputsVisited =
            visitedRef.current.day &&
            visitedRef.current.month &&
            visitedRef.current.year;
        const atLeastOneInputVisited =
            visitedRef.current.day ||
            visitedRef.current.month ||
            visitedRef.current.year;
        if (allInputsVisited) {
            clearAllFieldsOrIgnore();
        } else if (atLeastOneInputVisited && !isDOBElementFocused) {
            clearAllFieldsOrIgnore();
        }
    }, [activeElement, isDOBElementFocused]);

    useEffect(() => {
        const allInputsFilledOut =
            dobObj.current.day.value.length > 0 &&
            dobObj.current.month.value.length > 0 &&
            dobObj.current.year.value.length > 0;
        if (allInputsFilledOut) {
            clearAllFieldsOrIgnore();
        }
    }, [props.applicantStateOrAu]);

    const clearAllFieldsOrIgnore = () => {
        const isValid = isDateValid(
            props.value || '',
            props.applicantStateOrAu || '',
        );

        if (props.valid) {
            props.valid[1](isValid);
        }
        const activeElementId = activeElement?.id || '';

        if (props.value && props.value.length === 10) {
            visitedRef.current = {
                day: true,
                month: true,
                year: true,
                isInside: false, //false bc active element does not go from date input field to date input field.
            };
        }

        //On each date field, onblur goes first to body element, and then focus,
        visitedRef.current.isInside =
            activeElementId?.includes('dob__') ||
            activeElementId?.includes('dateOfBirthPQDE') ||
            activeElementId?.includes('au') ||
            isTabKey ||
            clickedInOtherDateField;

        setShowIcon(isValid && !visitedRef.current.isInside && inputInteracted);
    };

    const handleOnBlur = (event: React.FocusEvent<any>) => {
        if (event.relatedTarget instanceof HTMLInputElement &&
            isElementIncluded(event.relatedTarget.id)) {
            setIsDOBElementFocused(true);
        } else {
            setIsDOBElementFocused(false);
        }
        const input: 'day' | 'month' | 'year' =
            event.target.id.match(/day|month|year/)[0];
        visitedRef.current[input] = true;
        if (props.setValueCallback) {
            if(dobObj.current.day.value.trim() || dobObj.current.month.value.trim() || dobObj.current.year.value.trim()){
                props.setValueCallback(getFormattedDate(dobObj.current));
            } else {
                props.setValueCallback('');
            }
        }
        setClickedInOtherDateField(isMouseDown);
    };


    return (
        <>
            <InputDate
                {...props}
                isAuthorizedUser={props.isAuthorizedUser}
                value={props.value}
                setValue={(val: string) => {
                    if(dobObj.current.day.value.trim() || dobObj.current.month.value.trim() || dobObj.current.year.value.trim()){
                        props.setValueCallback(getFormattedDate(dobObj.current));
                    } else {
                        props.setValueCallback(val);
                    }
                }}
                ref={inputRef}
                onKeyDown={(e: any) => {
                    if (
                        (e.shiftKey && activeElement?.id.includes('month')) ||
                        (!e.shiftKey && activeElement?.id.includes('year'))
                    ) {
                        setIsTabKey(false);
                    } else if (e.key === 'Tab') {
                        setIsTabKey(true);
                    }
                    if (e.key === 'Enter' && props.validateWithEnter) {
                        clearAllFieldsOrIgnore();
                    }
                }}
                onKeyUp={() => {
                    //needs to reset this value after set on keydown
                    setIsTabKey(false);
                }}
                ageMax={props.ageMax || 101}
                ageMin={props.ageMin || minAgeForStateOrAuthorized}
                onMouseDown={() => {
                    setIsMouseDown(true);
                }}
                onMouseUp={() => {
                    setIsMouseDown(false);
                }}
                onFocus={(e: React.FocusEvent<any>) => {
                    setIsDOBElementFocused(true);
                    setShowIcon(false);
                    setInputInteracted(true);
                    if (props.onFocusCallback) {
                        props.onFocusCallback(e);
                    }
                }}
                onBlur={(e: React.FocusEvent<any>) => {
                    handleOnBlur(e);
                    if (props.onBlurCallback) {
                        props.onBlurCallback(e);
                    }
                }}
                isShowingHelperText
                advancedMasking={props.advancedMasking}
                useSuccessIcon={props.useSuccessIcon}
                showExclamationError
                showSuccessIcon={
                    props.showSuccessIcon !== undefined ? props.showSuccessIcon : showIcon
                }
                error={!props.valid[0] || props.error}
            />
        </>
    );
};

export default DateOfBirthField;
